import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import Scroll from 'react-scroll';
import { useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan, faUsers, faPencil, faTasks, faTachometer, faPlus, faEye, faLungs, faCheckCircle, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import Select from 'react-select'
import { useRecoilState } from 'recoil';

import { topAlertMessageState, topAlertStatusState, userDetailState } from "../../../../AppState";
import { putSessionUpdateAPI } from "../../../../API/Session";
import { getSessionDetailAPI } from "../../../../API/Session";
import FormErrorBox from "../../../Reusable/FormErrorBox";
import DataDisplayRowText from "../../../Reusable/DataDisplayRowText";
import FormMultiSelectField from "../../../Reusable/FormMultiSelectField";
import DataDisplayRowSelect from "../../../Reusable/DataDisplayRowSelect";
import { BREATHWORK_TYPE_WITH_EMPTY_OPTIONS } from "../../../../Constants/FieldOptions";


function UserSessionBreathworkOptionDetail() {
    ////
    //// URL parameters.
    ////

    const { uid, sid, boid } = useParams()

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);
    const [user, setUser] = useRecoilState(userDetailState);

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [isFetching, setFetching] = useState(false);
    const [forceURL, setForceURL] = useState("");
    const [session, setSession] = useState({});
    const [name, setName] = useState();
    const [maxMins, setMaxMins] = useState();
    const [instructions, setInstructions] = useState([]);
    const [inhaleHold, setInhaleHold] = useState();
    const [inhale, setInhale] = useState();
    const [exhaleHold, setExhaleHold] = useState();
    const [exhale, setExhale] = useState();
    const [description, setDescription] = useState();
    const [breathworkType, setBreathworkType] = useState("");
    const [backgroundVideoURL, setBackgroundVideoURL] = useState();
    // const [pickImageModalIsVisible, setPickCMSImageModalIsVisible] = useState(false);ç
    const [pickBackgroundVideoURLModalIsVisible, setPickBackgroundVideoURLModalIsVisible] = useState(false);
    const [showDeletionConfirmationModal, setShowDeletionConfirmationModal] = useState(false);


    ////
    //// Event handling.
    ////

    const onDeleteConfirmButtonClick = () => {
        console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

        // Remove the item at this location.
        session.breathworkOptions.splice(boid, 1);

        putSessionUpdateAPI(
            session,
            onSessionUpdateSuccess,
            onSessionUpdateError,
            onSessionUpdateDone
        );
        setShowDeletionConfirmationModal(false);
    }

    ////
    //// API.
    ////

    function onSessionDetailSuccess(response){
        console.log("onSessionDetailSuccess: Starting...");
        console.log("onSessionDetailSuccess: data:", response);
        setSession(response);

        // Defensive code: The following code will extract the video section from the session, else return null.
        if (response !== undefined && response !== null && response !== "") {
            if (response.breathworkOptions !== undefined && response.breathworkOptions !== null && response.breathworkOptions !== "") {
                let breathworkOption = response.breathworkOptions[boid];
                if (breathworkOption !== undefined && breathworkOption !== null && breathworkOption !== "") {
                    setName(breathworkOption.name);
                    setMaxMins(breathworkOption.maxMins);
                    setInstructions(breathworkOption.instructions);
                    setInhaleHold(breathworkOption.inhaleHold);
                    setInhale(breathworkOption.inhale);
                    setExhaleHold(breathworkOption.exhaleHold);
                    setExhale(breathworkOption.exhale);
                    setDescription(breathworkOption.description);
                    setBreathworkType(breathworkOption.breathworkType);
                    setBackgroundVideoURL(breathworkOption.backgroundVideoUrl);
                }
            }
        }
    }

    function onSessionDetailError(apiErr) {
        console.log("onSessionDetailError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionDetailDone() {
        console.log("onSessionDetailDone: Starting...");
        setFetching(false);
    }

    function onSessionUpdateSuccess(response){
        // For debugging purposes only.
        console.log("onSessionUpdateSuccess: Starting...");
        console.log(response);

        // Add a temporary banner message in the app and then clear itself after 2 seconds.
        setTopAlertMessage("Breathwork options deleted");
        setTopAlertStatus("success");
        setTimeout(() => {
            console.log("onSessionUpdateSuccess: Delayed for 2 seconds.");
            console.log("onSessionUpdateSuccess: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Redirect the user to a new page.
        setForceURL("/user/" + uid + "/session/" + sid + "/breathwork-options");
    }

    function onSessionUpdateError(apiErr) {
        console.log("onSessionUpdateError: Starting...");
        setErrors(apiErr);

        // Add a temporary banner message in the app and then clear itself after 2 seconds.
        setTopAlertMessage("Failed submitting");
        setTopAlertStatus("danger");
        setTimeout(() => {
            console.log("onSessionUpdateError: Delayed for 2 seconds.");
            console.log("onSessionUpdateError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();

        // Reload the session upon errors.
        setFetching(true);
        getSessionDetailAPI(
            sid,
            onSessionDetailSuccess,
            onSessionDetailError,
            onSessionDetailDone
        );
    }

    function onSessionUpdateDone() {
        console.log("onSessionUpdateDone: Starting...");
        setFetching(false);
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            setFetching(true);
            getSessionDetailAPI(
                sid,
                onSessionDetailSuccess,
                onSessionDetailError,
                onSessionDetailDone
            );
        }

        return () => { mounted = false; }
    }, [sid]);

    ////
    //// Component rendering.
    ////

    if (forceURL !== "") {
        return <Navigate to={forceURL}  />
    }

    // Defensive code: The following code will extract the video section from the session, else return null.
    let sleepQuestion, id, title, subtitle = null;
    if (session !== undefined && session !== null && session !== "") {
        if (session.sleepQuestions !== undefined && session.sleepQuestions !== null && session.sleepQuestions !== "") {
            sleepQuestion = session.sleepQuestions[boid];
            if (sleepQuestion !== undefined && sleepQuestion !== null && sleepQuestion !== "") {
                id = sleepQuestion.id;
                title = sleepQuestion.title;
                subtitle = sleepQuestion.subtitle;
            }
        }
    }
    console.log(id, title, subtitle); // For debugging purposes only.

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/users" aria-current="page"><FontAwesomeIcon className="fas" icon={faUsers} />&nbsp;Users</Link></li>
                            <li class=""><Link to={`/user/${uid}/sessions`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Detail (Sessions)</Link></li>
                            <li class=""><Link to={`/user/${uid}/session/${sid}`} aria-current="page"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session</Link></li>
                            <li class=""><Link to={`/user/${uid}/session/${sid}/breathwork-options`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Detail (Breathwork Options)</Link></li>
                            <li class="is-active"><Link aria-current="page"><FontAwesomeIcon className="fas" icon={faLungs} />&nbsp;Breathwork Option</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/user/${uid}/session/${sid}`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Detail (Breathwork Options)</Link></li>
                        </ul>
                    </nav>

                    {/* Modal */}
                    <nav class="">
                        <div class={`modal ${showDeletionConfirmationModal ? 'is-active' : ''}`}>
                            <div class="modal-background"></div>
                            <div class="modal-card">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">Are you sure?</p>
                                    <button class="delete" aria-label="close" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}></button>
                                </header>
                                <section class="modal-card-body">
                                    You are about to delete this breathwork option and all the data inside of it. This action is cannot be undone. Are you sure you would like to continue?
                                </section>
                                <footer class="modal-card-foot">
                                    <button class="button is-success" onClick={onDeleteConfirmButtonClick}>Confirm</button>
                                    <button class="button" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}>Cancel</button>
                                </footer>
                            </div>
                        </div>
                    </nav>

                    {/* Page */}
                    <nav class="box">

                        {/* Title + Options */}
                        <div class="columns">
                            <div class="column">
                                <p class="title is-4"><FontAwesomeIcon className="fas" icon={faLungs} />&nbsp;Breathwork Option - {user.firstName} {user.lastName}</p>
                            </div>
                            <div class="column has-text-right">
                                <Link to={`/user/${uid}/session/${sid}/breathwork-option/${boid}/edit`} class="button is-small is-warning is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faPencil} />&nbsp;Edit Sleep Question
                                </Link>
                                &nbsp;
                                <button onClick={(e, ses) => setShowDeletionConfirmationModal(true)} class="button is-small is-danger is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faTrashCan} />&nbsp;Delete Sleep Question
                                </button>
                            </div>
                        </div>

                        <FormErrorBox errors={errors} />

                        <p class="subtitle is-6">Details</p>

                        {isFetching && <div class="columns is-centered" style={{paddingTop: "20px"}}>
                            <div class="column has-text-centered is-2">
                            <div class="loader-wrapper is-centered">
                              <div class="loader is-loading is-centered" style={{height: "80px", width: "80px"}}></div>
                            </div>
                            </div>
                        </div>}

                        {!isFetching && <div class="container">
                            <DataDisplayRowText
                                label="Name"
                                value={name}
                            />

                            <DataDisplayRowText
                                label="Max Mins (Optional)"
                                value={maxMins}
                            />

                            <DataDisplayRowText
                                label="Instructions"
                                type="textlist"
                                value={instructions}
                            />

                            <DataDisplayRowText
                                label="Inhale Hold (Optional)"
                                value={inhaleHold}
                            />

                            <DataDisplayRowText
                                label="Inhale (Optional)"
                                value={inhale}
                            />

                            <DataDisplayRowText
                                label="Exhale Hold (Optional)"
                                value={exhaleHold}
                            />

                            <DataDisplayRowText
                                label="Exhale (Optional)"
                                value={exhale}
                            />

                            <DataDisplayRowText
                                label="Description"
                                value={description}
                            />

                            <DataDisplayRowSelect
                                label="Breathwork Type"
                                selectedValue={breathworkType}
                                options={BREATHWORK_TYPE_WITH_EMPTY_OPTIONS}
                            />

                            <DataDisplayRowText
                                label="Background Video URL"
                                value={backgroundVideoURL}
                            />

                            <div class="columns">
                                <div class="column is-half">
                                    <Link to={`/user/${uid}/session/${sid}/breathwork-options`} class="button is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back</Link>
                                </div>
                                <div class="column is-half has-text-right">
                                    <Link to={`/user/${uid}/session/${sid}/breathwork-option/${boid}/edit`} class="button is-warning is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</Link>
                                </div>
                            </div>

                        </div>}
                    </nav>
                </section>
            </div>
        </>
    );
}

export default UserSessionBreathworkOptionDetail;
